import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Full-screen media viewer.
 * - Images: object-contain (full aspect ratio preserved, no stretching).
 * - Videos: native controls, autoplay, full aspect ratio.
 * - Esc / backdrop / close button to dismiss; prev/next for galleries.
 */
export default function MediaLightbox({ open, items = [], startIndex = 0, onClose }) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => { if (open) setIndex(startIndex); }, [open, startIndex]);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight' && items.length > 1) go(1);
      else if (e.key === 'ArrowLeft' && items.length > 1) go(-1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, items.length, go, onClose]);

  if (!open || items.length === 0) return null;
  const current = items[index];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      onClick={onClose}
      data-testid="media-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
        data-testid="lightbox-close"
        aria-label="Close"
      >
        <X className="w-6 h-6" />
      </button>

      {items.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-3 md:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-3 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {index + 1} / {items.length}
          </span>
        </>
      )}

      <div className="max-w-[92vw] max-h-[88vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {current.type === 'video' ? (
          <video
            key={current.url}
            src={current.url}
            className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-lg"
            controls
            autoPlay
            playsInline
            data-testid="lightbox-video"
          />
        ) : (
          <img
            src={current.url}
            alt=""
            className="max-w-[92vw] max-h-[88vh] w-auto h-auto object-contain rounded-lg"
            data-testid="lightbox-image"
          />
        )}
      </div>
    </div>,
    document.body
  );
}
